import React, { useContext, useEffect, useRef } from "react";
import { DataContext } from "../context/DataContext";
import VerificationProcessingScreen from "./verification-processing-screen";
import { getEdgeGetSessionUrl } from "../utils/edge";
import { suppliedImages } from "../utils/suppliedImages";
import { trackEvent } from "../utils/telemetry";

// Re-check cadence: quick while the publisher is most likely mid-upload,
// then slower so a link opened long before the upload stays cheap.
const FAST_POLL_MS = 3000;
const SLOW_POLL_MS = 10000;
const FAST_WINDOW_MS = 60000;

/**
 * Journey API prefill: the publisher declared images at create_journey
 * but has not confirmed the upload yet (settings.images_received_at is
 * null). Hold the user here instead of opening capture, re-reading the
 * session until images_received_at lands, then hand the step back to the
 * route component with the updated settings. See utils/suppliedImages.js.
 */
export default function SuppliedImagesPending() {
  const { settings, dispatch } = useContext(DataContext);
  const requestId = settings?.request_id;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  useEffect(() => {
    if (!requestId) return undefined;
    let cancelled = false;
    let timer = null;
    const startedAt = Date.now();
    trackEvent(
      "supplied_images_waiting",
      { supplied_images: suppliedImages(settingsRef.current).join(",") },
      { once: true }
    );

    const check = async () => {
      try {
        const res = await fetch(`${getEdgeGetSessionUrl()}/${requestId}`, { method: "GET" });
        if (res.ok) {
          const result = await res.json();
          const receivedAt = result?.data?.settings?.images_received_at;
          if (!cancelled && receivedAt) {
            trackEvent("supplied_images_received", { wait_ms: Date.now() - startedAt });
            dispatch({
              type: "SET_SETTINGS",
              payload: { ...settingsRef.current, images_received_at: receivedAt },
            });
            return;
          }
        }
      } catch (err) {
        // Network blip: keep waiting, the next check retries.
      }
      if (cancelled) return;
      const elapsed = Date.now() - startedAt;
      timer = setTimeout(check, elapsed < FAST_WINDOW_MS ? FAST_POLL_MS : SLOW_POLL_MS);
    };

    timer = setTimeout(check, FAST_POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [requestId, dispatch]);

  return <VerificationProcessingScreen type="receiving-images" />;
}
